import React, { useEffect, useState } from 'react';
import api from '../api';
import toast from 'react-hot-toast';
import { useConfirm } from '../context/ConfirmContext';
import { Link, Trash2, Plus, RefreshCw, AlertCircle, ExternalLink, CheckCircle2, Image } from 'lucide-react';

const InstagramIcon = ({ size = 20, style = {} }) => (
    <svg 
        xmlns="http://www.w3.org/2000/svg" 
        width={size} 
        height={size} 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="currentColor" 
        strokeWidth="2" 
        strokeLinecap="round" 
        strokeLinejoin="round"
        style={style}
    >
        <rect width="20" height="20" x="2" y="2" rx="5" ry="5"/>
        <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
        <line x1="17.5" x2="17.51" y1="6.5" y2="6.5"/>
    </svg>
);

const InstagramLinksPage = () => {
    const confirm = useConfirm();
    const [recentMedia, setRecentMedia] = useState([]);
    const [linkedPosts, setLinkedPosts] = useState([]);
    const [loadingMedia, setLoadingMedia] = useState(false);
    const [loadingLinked, setLoadingLinked] = useState(false);
    const [submittingLink, setSubmittingLink] = useState(false);

    // Form manual
    const [manualMediaId, setManualMediaId] = useState('');
    const [manualProductLink, setManualProductLink] = useState('');
    const [manualProductName, setManualProductName] = useState('');
    const [manualPrice, setManualPrice] = useState('');

    // Modal / Controle de Vinculação para post específico
    const [selectedMedia, setSelectedMedia] = useState(null);
    const [productLink, setProductLink] = useState('');
    const [productName, setProductName] = useState('');
    const [price, setPrice] = useState('');
    const [productImageUrl, setProductImageUrl] = useState('');

    // Form manual
    const [manualImageUrl, setManualImageUrl] = useState('');

    // Modal de Cadastro Manual e Paginação
    const [isManualModalOpen, setIsManualModalOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        carregarDados();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const carregarDados = async () => {
        carregarLinkedPosts();
        carregarRecentMedia();
    };

    const carregarLinkedPosts = async () => {
        setLoadingLinked(true);
        try {
            const res = await api.get('/instagram/linked-posts');
            setLinkedPosts(res.data);
            setCurrentPage(1);
        } catch (err) {
            console.error("Erro ao carregar posts vinculados:", err);
        } finally {
            setLoadingLinked(false);
        }
    };

    const carregarRecentMedia = async () => {
        setLoadingMedia(true);
        try {
            const res = await api.get('/instagram/recent-media');
            setRecentMedia(res.data);
        } catch (err) {
            console.warn("Instagram Business Account não configurado ou erro ao buscar mídias:", err.response?.data?.error || err.message);
        } finally {
            setLoadingMedia(false);
        }
    };

    const handleVincularPost = async (e) => {
        e.preventDefault();
        const mediaIdToUse = selectedMedia ? selectedMedia.id : manualMediaId;
        const linkToUse = selectedMedia ? productLink : manualProductLink;
        const nameToUse = selectedMedia ? productName : manualProductName;
        const priceToUse = selectedMedia ? price : manualPrice;

        if (!mediaIdToUse || !linkToUse) {
            return toast.error("Por favor, preencha o ID do Post e o Link do Produto.");
        }

        setSubmittingLink(true);
        const loadToast = toast.loading("Processando e convertendo link...");

        try {
            const res = await api.post('/instagram/link-post', {
                mediaId: mediaIdToUse,
                productLink: linkToUse,
                productName: nameToUse,
                price: priceToUse,
                imageUrl: selectedMedia ? productImageUrl : manualImageUrl,
                tipo: selectedMedia ? (selectedMedia.media_type === 'STORY' ? 'story' : 'feed') : 'feed'
            });

            toast.success(res.data.message || "Link associado com sucesso!", { id: loadToast });
            
            // Limpa formulários
            setManualMediaId('');
            setManualProductLink('');
            setManualProductName('');
            setManualPrice('');
            setManualImageUrl('');
            setSelectedMedia(null);
            setProductLink('');
            setProductName('');
            setPrice('');
            setProductImageUrl('');
            setIsManualModalOpen(false);

            // Recarrega os dados
            carregarDados();
        } catch (err) {
            const msg = err.response?.data?.error || "Erro ao associar link.";
            toast.error(msg, { id: loadToast });
        } finally {
            setSubmittingLink(false);
        }
    };

    const handleDesvincular = async (mediaId) => {
        const confirmed = await confirm({
            title: "Remover Vinculação",
            message: "Deseja realmente remover a vinculação deste post do Instagram? A resposta automática será desativada para ele.",
            confirmText: "Remover",
            cancelText: "Cancelar",
            type: "danger"
        });
        if (!confirmed) return;

        const loadToast = toast.loading("Removendo vinculação...");
        try {
            await api.delete(`/instagram/linked-posts/${encodeURIComponent(mediaId)}`);
            toast.success("Vinculação removida com sucesso!", { id: loadToast });
            carregarDados();
        } catch (err) {
            toast.error("Erro ao remover vinculação.", { id: loadToast });
        }
    };

    // Auxiliar para achar a vinculação de uma mídia
    const getVinculacao = (mediaId) => {
        return linkedPosts.find(p => p.mediaId === mediaId);
    };

    return (
        <div style={{ maxWidth: '1200px', margin: '0 auto', fontFamily: 'Inter, sans-serif' }}>
            {/* Cabeçalho */}
            <div className="instagram-header">
                <div>
                    <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#0f172a', margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <InstagramIcon size={32} style={{ color: '#e1306c' }} /> Automação de Posts Manuais
                    </h1>
                    <p style={{ color: '#64748b', fontSize: '15px', marginTop: '5px' }}>
                        Vincule posts manuais do seu Instagram a links de afiliados. Quando alguém comentar "QUERO", o robô enviará o link correto.
                    </p>
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                        onClick={() => setIsManualModalOpen(true)}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 18px', backgroundColor: '#ee4d2d',
                            border: 'none', borderRadius: '8px', color: 'white', fontWeight: '600', cursor: 'pointer',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)', transition: '0.2s'
                        }}
                    >
                        <Plus size={16} /> Cadastro Manual
                    </button>
                    <button
                        onClick={carregarDados}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 18px', backgroundColor: 'white',
                            border: '1px solid #e2e8f0', borderRadius: '8px', color: '#475569', fontWeight: '600', cursor: 'pointer',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.02)', transition: '0.2s'
                        }}
                    >
                        <RefreshCw size={16} /> Atualizar
                    </button>
                </div>
            </div>

            <div className="instagram-grid">
                {/* Lado Esquerdo: Mídias do Instagram ou Posts já Vinculados */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
                    {/* Lista do Feed Recente do Instagram */}
                    <div style={{ backgroundColor: 'white', padding: '24px', borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                        <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b', marginBottom: '18px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <InstagramIcon size={20} style={{ color: '#e1306c' }} /> Posts Recentes do seu Instagram
                        </h2>

                        {loadingMedia ? (
                            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                                <RefreshCw size={32} className="animate-spin" style={{ color: '#e1306c' }} />
                            </div>
                        ) : recentMedia.length === 0 ? (
                            <div style={{ padding: '30px', textAlign: 'center', backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px dashed #cbd5e1' }}>
                                <AlertCircle size={32} style={{ color: '#64748b', margin: '0 auto 10px' }} />
                                <p style={{ color: '#475569', fontWeight: '600', margin: 0 }}>Nenhum post recente carregado</p>
                                <p style={{ color: '#64748b', fontSize: '13px', margin: '5px 0 0' }}>
                                    Certifique-se de que o ID Comercial do Instagram e o Token estão corretos nas Configurações.
                                </p>
                            </div>
                        ) : (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '15px' }}>
                                {recentMedia.map(media => {
                                    const vinc = getVinculacao(media.id);
                                    return (
                                        <div
                                            key={media.id}
                                            style={{
                                                border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden', display: 'flex', flexDirection: 'column',
                                                backgroundColor: '#f8fafc', transition: '0.2s', position: 'relative'
                                            }}
                                        >
                                            {/* Thumbnail / Image */}
                                            <div style={{ position: 'relative', width: '100%', paddingBottom: '100%', overflow: 'hidden', backgroundColor: '#000' }}>
                                                {media.media_url ? (
                                                    <img
                                                        src={media.media_type === 'VIDEO' ? (media.thumbnail_url || media.media_url) : media.media_url}
                                                        alt="Instagram"
                                                        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                                                    />
                                                ) : (
                                                    <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
                                                        <Image size={32} />
                                                    </div>
                                                )}

                                                <span style={{
                                                    position: 'absolute', top: '8px', right: '8px', fontSize: '10px', fontWeight: 'bold',
                                                    padding: '3px 6px', borderRadius: '4px', color: 'white',
                                                    backgroundColor: media.media_type === 'STORY' ? '#8b5cf6' : '#f43f5e'
                                                }}>
                                                    {media.media_type || 'POST'}
                                                </span>
                                            </div>

                                            {/* Info */}
                                            <div style={{ padding: '10px', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                                <div>
                                                    <p style={{
                                                        fontSize: '12px', color: '#334155', margin: 0, display: '-webkit-box',
                                                        WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', height: '32px',
                                                        lineHeight: '16px'
                                                    }}>
                                                        {media.caption || <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>Sem legenda</span>}
                                                    </p>
                                                    <span style={{ fontSize: '10px', color: '#94a3b8', display: 'block', marginTop: '5px' }}>
                                                        {new Date(media.timestamp).toLocaleDateString()}
                                                    </span>
                                                </div>

                                                {/* Vinculação ou Botão */}
                                                <div style={{ marginTop: '10px' }}>
                                                    {vinc ? (
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                                            <div style={{
                                                                backgroundColor: '#dcfce7', color: '#15803d', padding: '6px', borderRadius: '6px',
                                                                fontSize: '11px', fontWeight: '700', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px'
                                                            }}>
                                                                <CheckCircle2 size={12} /> IA Ativa
                                                            </div>
                                                            <button
                                                                onClick={() => handleDesvincular(media.id)}
                                                                style={{
                                                                    width: '100%', padding: '5px', backgroundColor: 'transparent', border: '1px solid #fca5a5',
                                                                    color: '#dc2626', borderRadius: '6px', fontSize: '11px', fontWeight: '600', cursor: 'pointer'
                                                                }}
                                                            >
                                                                Desvincular
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <button
                                                            onClick={() => {
                                                                setSelectedMedia(media);
                                                                setProductLink('');
                                                                setProductName('');
                                                                setPrice('');
                                                                setProductImageUrl('');
                                                            }}
                                                            style={{
                                                                width: '100%', padding: '8px', backgroundColor: '#ee4d2d', border: 'none',
                                                                color: 'white', borderRadius: '6px', fontSize: '12px', fontWeight: '700', cursor: 'pointer',
                                                                transition: '0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px'
                                                            }}
                                                        >
                                                            <Plus size={14} /> Vincular Link
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Todos os Links Vinculados Ativos */}
                    <div style={{ backgroundColor: 'white', padding: '24px', borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                        <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b', marginBottom: '18px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Link size={20} style={{ color: '#ee4d2d' }} /> Todos os Posts Vinculados ({linkedPosts.length})
                        </h2>

                        {loadingLinked ? (
                            <div style={{ display: 'flex', justifyContent: 'center', padding: '30px' }}>
                                <RefreshCw size={24} className="animate-spin" style={{ color: '#ee4d2d' }} />
                            </div>
                        ) : linkedPosts.length === 0 ? (
                            <div style={{ padding: '24px', textAlign: 'center', color: '#94a3b8', fontSize: '14px' }}>
                                Nenhum post foi vinculado ainda. Use o grid acima ou o formulário manual ao lado.
                            </div>
                        ) : (
                            <div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    {linkedPosts.slice((currentPage - 1) * 5, currentPage * 5).map(post => (
                                        <div className="instagram-linked-post"
                                            key={post.id}
                                            style={{
                                                padding: '12px 18px',
                                                backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', transition: '0.2s'
                                            }}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                <div style={{
                                                    backgroundColor: '#ffedd5', color: '#ea580c', padding: '8px', borderRadius: '8px',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '12px'
                                                }}>
                                                    {post.loja ? post.loja.substring(0, 3) : 'DES'}
                                                </div>
                                                <div>
                                                    <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: '#1e293b' }}>
                                                        {post.productName}
                                                    </h4>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '3px', flexWrap: 'wrap' }}>
                                                        <span style={{ fontSize: '12px', color: '#f43f5e', fontWeight: '700' }}>
                                                            R$ {post.price || 'Consultar'}
                                                        </span>
                                                        <span style={{ fontSize: '11px', color: '#10b981', fontWeight: '700', backgroundColor: '#ecfdf5', padding: '2px 6px', borderRadius: '4px', border: '1px solid #a7f3d0' }} title="Digite esse código na busca da vitrine para achar o produto">
                                                            Ref: #{post.id}
                                                        </span>
                                                        <span style={{ fontSize: '11px', color: '#64748b' }}>
                                                            ID Post: {post.mediaId}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="instagram-linked-post-actions" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                 <button
                                                     onClick={() => {
                                                         navigator.clipboard.writeText(post.link);
                                                         toast.success("Link do produto copiado!");
                                                     }}
                                                     style={{
                                                         display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 12px',
                                                         backgroundColor: '#ecfdf5', border: '1px solid #10b981', borderRadius: '6px',
                                                         color: '#065f46', fontSize: '12px', fontWeight: '600', cursor: 'pointer'
                                                     }}
                                                 >
                                                     Copiar Link 🔗
                                                 </button>
                                                <a
                                                    href={post.link}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{
                                                        display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 12px',
                                                        backgroundColor: 'white', border: '1px solid #cbd5e1', borderRadius: '6px',
                                                        color: '#334155', fontSize: '12px', fontWeight: '600', textDecoration: 'none'
                                                    }}
                                                >
                                                    Ver Link <ExternalLink size={12} />
                                                </a>
                                                <button
                                                    onClick={() => handleDesvincular(post.mediaId)}
                                                    style={{
                                                        padding: '8px', backgroundColor: 'transparent', border: 'none',
                                                        color: '#ef4444', cursor: 'pointer', borderRadius: '6px', transition: '0.2s',
                                                        hover: { backgroundColor: '#fee2e2' }
                                                    }}
                                                    title="Remover Vinculação"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {Math.ceil(linkedPosts.length / 5) > 1 && (
                                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', marginTop: '20px' }}>
                                        <button
                                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                            disabled={currentPage === 1}
                                            style={{
                                                padding: '6px 12px',
                                                borderRadius: '6px',
                                                border: '1px solid #cbd5e1',
                                                backgroundColor: 'white',
                                                color: currentPage === 1 ? '#94a3b8' : '#334155',
                                                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                                                fontSize: '13px',
                                                fontWeight: '600'
                                            }}
                                        >
                                            Anterior
                                        </button>
                                        <span style={{ fontSize: '13px', color: '#475569', fontWeight: '500' }}>
                                            Página {currentPage} de {Math.ceil(linkedPosts.length / 5)}
                                        </span>
                                        <button
                                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(linkedPosts.length / 5)))}
                                            disabled={currentPage === Math.ceil(linkedPosts.length / 5)}
                                            style={{
                                                padding: '6px 12px',
                                                borderRadius: '6px',
                                                border: '1px solid #cbd5e1',
                                                backgroundColor: 'white',
                                                color: currentPage === Math.ceil(linkedPosts.length / 5) ? '#94a3b8' : '#334155',
                                                cursor: currentPage === Math.ceil(linkedPosts.length / 5) ? 'not-allowed' : 'pointer',
                                                fontSize: '13px',
                                                fontWeight: '600'
                                            }}
                                        >
                                            Próxima
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Lado Direito: Formulário Manual ou Modal do Post Selecionado */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
                    {selectedMedia ? (
                        /* Formulário Vinculador Ativo para Item Selecionado */
                        <div style={{ backgroundColor: 'white', padding: '24px', borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', border: '2px solid #ee4d2d' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
                                <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#1e293b', margin: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <Link size={18} style={{ color: '#ee4d2d' }} /> Vincular Link ao Post
                                </h3>
                                <button
                                    onClick={() => setSelectedMedia(null)}
                                    style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontWeight: 'bold' }}
                                >
                                    Cancelar
                                </button>
                            </div>

                            {/* Detalhes do Post Selecionado */}
                            <div style={{ display: 'flex', gap: '10px', backgroundColor: '#f8fafc', padding: '10px', borderRadius: '8px', marginBottom: '18px' }}>
                                <img
                                    src={selectedMedia.thumbnail_url || selectedMedia.media_url}
                                    alt="Post"
                                    style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '6px' }}
                                />
                                <div>
                                    <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#8b5cf6', textTransform: 'uppercase' }}>
                                        {selectedMedia.media_type}
                                    </span>
                                    <p style={{
                                        fontSize: '11px', color: '#475569', margin: '3px 0 0', display: '-webkit-box',
                                        WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden'
                                    }}>
                                        {selectedMedia.caption || "Sem legenda"}
                                    </p>
                                </div>
                            </div>

                            <form onSubmit={handleVincularPost} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
                                        Link do Produto *
                                    </label>
                                    <input
                                        type="url"
                                        placeholder="Pape link Shopee, ML, AliExpress..."
                                        value={productLink}
                                        onChange={(e) => setProductLink(e.target.value)}
                                        required
                                        style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', boxSizing: 'border-box' }}
                                    />
                                    <span style={{ fontSize: '11px', color: '#64748b', display: 'block', marginTop: '4px' }}>
                                        Nosso sistema converterá o link para afiliado automaticamente.
                                    </span>
                                </div>

                                <div>
                                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
                                        Nome do Produto (Opcional)
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="Caso queira definir um nome personalizado"
                                        value={productName}
                                        onChange={(e) => setProductName(e.target.value)}
                                        style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', boxSizing: 'border-box' }}
                                    />
                                </div>

                                <div>
                                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
                                        Preço R$ (Opcional)
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="Ex: 49,90"
                                        value={price}
                                        onChange={(e) => setPrice(e.target.value)}
                                        style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', boxSizing: 'border-box' }}
                                    />
                                </div>

                                <button
                                    type="submit"
                                    disabled={submittingLink}
                                    style={{
                                        width: '100%', padding: '12px', backgroundColor: '#ee4d2d', color: 'white', border: 'none',
                                        borderRadius: '8px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center',
                                        justifyContent: 'center', gap: '6px', marginTop: '10px', opacity: submittingLink ? 0.7 : 1
                                    }}
                                >
                                    {submittingLink ? <RefreshCw size={16} className="animate-spin" /> : <Plus size={16} />}
                                    Confirmar Vinculação
                                </button>
                            </form>
                        </div>
                    ) : (
                        /* Formulário de Cadastro Manual Puro (Para Posts antigos ou não listados) */
                        <div style={{ backgroundColor: 'white', padding: '24px', borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                            <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#1e293b', marginBottom: '18px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Plus size={18} style={{ color: '#ee4d2d' }} /> Cadastro Manual
                            </h3>
                            <p style={{ fontSize: '12px', color: '#64748b', marginTop: '-10px', marginBottom: '15px' }}>
                                Use se o post for antigo ou se o Reels/Vídeo não estiver aparecendo na lista ao lado.
                            </p>

                            <form onSubmit={handleVincularPost} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
                                        ID do Post ou URL do Post *
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="Ex: 180123456789 ou Link completo"
                                        value={manualMediaId}
                                        onChange={(e) => setManualMediaId(e.target.value)}
                                        required
                                        style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', boxSizing: 'border-box' }}
                                    />
                                    <span style={{ fontSize: '11px', color: '#64748b', display: 'block', marginTop: '4px' }}>
                                        O ID do post comercial ou o link direto da publicação.
                                    </span>
                                </div>

                                <div>
                                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
                                        Link do Produto *
                                    </label>
                                    <input
                                        type="url"
                                        placeholder="Link Shopee, Mercado Livre..."
                                        value={manualProductLink}
                                        onChange={(e) => setManualProductLink(e.target.value)}
                                        required
                                        style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', boxSizing: 'border-box' }}
                                    />
                                </div>

                                <div>
                                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
                                        Nome do Produto (Opcional)
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="Ex: Garrafa Térmica Cabo Madeira"
                                        value={manualProductName}
                                        onChange={(e) => setManualProductName(e.target.value)}
                                        style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', boxSizing: 'border-box' }}
                                    />
                                </div>

                                <div>
                                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
                                        Preço R$ (Opcional)
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="Ex: 79,90"
                                        value={manualPrice}
                                        onChange={(e) => setManualPrice(e.target.value)}
                                        style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', boxSizing: 'border-box' }}
                                    />
                                </div>

                                <button
                                    type="submit"
                                    disabled={submittingLink}
                                    style={{
                                        width: '100%', padding: '12px', backgroundColor: '#ee4d2d', color: 'white', border: 'none',
                                        borderRadius: '8px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center',
                                        justifyContent: 'center', gap: '6px', marginTop: '10px', opacity: submittingLink ? 0.7 : 1
                                    }}
                                >
                                    {submittingLink ? <RefreshCw size={16} className="animate-spin" /> : <Plus size={16} />}
                                    Vincular Post Manual
                                </button>
                            </form>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal de Cadastro Manual para Mobile/Topo */}
            {isManualModalOpen && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(15, 23, 42, 0.6)',
                    backdropFilter: 'blur(4px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000,
                    padding: '20px'
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '16px',
                        boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
                        width: '100%',
                        maxWidth: '500px',
                        padding: '24px',
                        position: 'relative',
                        animation: 'fadeIn 0.2s ease-out'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
                            <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Plus size={20} style={{ color: '#ee4d2d' }} /> Cadastro Manual de Link
                            </h3>
                            <button
                                onClick={() => setIsManualModalOpen(false)}
                                style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '20px', fontWeight: 'bold' }}
                            >
                                &times;
                            </button>
                        </div>
                        
                        <form onSubmit={handleVincularPost} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                            <div>
                                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
                                    ID do Post ou URL do Post *
                                </label>
                                <input
                                    type="text"
                                    placeholder="Ex: 180123456789 ou Link completo"
                                    value={manualMediaId}
                                    onChange={(e) => setManualMediaId(e.target.value)}
                                    required
                                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', boxSizing: 'border-box' }}
                                />
                            </div>

                            <div>
                                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
                                    Link do Produto *
                                </label>
                                <input
                                    type="url"
                                    placeholder="Link Shopee, Mercado Livre..."
                                    value={manualProductLink}
                                    onChange={(e) => setManualProductLink(e.target.value)}
                                    required
                                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', boxSizing: 'border-box' }}
                                />
                            </div>

                            <div>
                                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
                                    Nome do Produto (Opcional)
                                </label>
                                <input
                                    type="text"
                                    placeholder="Ex: Garrafa Térmica"
                                    value={manualProductName}
                                    onChange={(e) => setManualProductName(e.target.value)}
                                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', boxSizing: 'border-box' }}
                                />
                            </div>

                            <div>
                                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
                                    Preço R$ (Opcional)
                                </label>
                                <input
                                    type="text"
                                    placeholder="Ex: 79,90"
                                    value={manualPrice}
                                    onChange={(e) => setManualPrice(e.target.value)}
                                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', boxSizing: 'border-box' }}
                                />
                            </div>

                            <button
                                type="submit"
                                disabled={submittingLink}
                                style={{
                                    width: '100%', padding: '12px', backgroundColor: '#ee4d2d', color: 'white', border: 'none',
                                    borderRadius: '8px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center',
                                    justifyContent: 'center', gap: '6px', marginTop: '10px', opacity: submittingLink ? 0.7 : 1
                                }}
                            >
                                {submittingLink ? <RefreshCw size={16} className="animate-spin" /> : <Plus size={16} />}
                                Vincular Post Manual
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InstagramLinksPage;
